import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { isMT4PlatformType, isMT5PlatformType, isTMTPlatformType } from '@model/PlatformTypeEnum'
import { TickmillProductType } from '@model/TickmillProductType'
import { TradingAccountInfo } from '@model/TradingAccountInfo'

import { AuthSessionContext } from '../AuthContext'
import { useProductReadContext } from '../ProductContext'
import {
  AccountInfoContextHook,
  AccountInfoContextValue,
  AccountInfoLimits,
  AccountInfoLimitsState,
  initialAccountInfoLimitsState,
} from './AccountInfoContext.types'

const AccountInfoLimitsContext = createContext<AccountInfoContextValue | undefined>(undefined)

export const AccountInfoLimitsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()
  const [auth] = useContext(AuthSessionContext)
  const queryClient = useQueryClient()

  const processAccountInfo = useCallback(
    (accountInfo: TradingAccountInfo[], product?: TickmillProductType): AccountInfoLimits => {
      const tmtInfo = accountInfo?.find((info) => isTMTPlatformType(info.platform.id))

      return {
        mt4:
          accountInfo?.find((info) => isMT4PlatformType(info.platform.id))?.maxAccountsAllowed ?? 0,

        mt5:
          accountInfo?.find((info) => isMT5PlatformType(info.platform.id))?.maxAccountsAllowed ?? 0,

        tmt: tmtInfo?.maxAccountsAllowed ?? 0,

        maxTotalLimit:
          accountInfo
            .filter((i) => i.product.id === product)
            ?.reduce((acc, info) => acc + info.maxAccountsAllowed, 0) ?? 0,

        hasReachedMaxTradingAccounts: !accountInfo
          .filter((x) => product === x.product.id || !product)
          .some((x) => x.allowToOpenAccount),

        isDemoTickmillTraderCreateAllowed: accountInfo?.some(
          (account) => isTMTPlatformType(account.platform.id) && account.allowToOpenDemoAccount
        ),

        isMTCreateAllowed: accountInfo?.some(
          (account) =>
            (isMT4PlatformType(account.platform.id) || isMT5PlatformType(account.platform.id)) &&
            account.allowToOpenAccount
        ),

        isTickmillTraderCreateAllowed: accountInfo?.some(
          (account) => isTMTPlatformType(account.platform.id) && account.allowToOpenAccount
        ),

        isTickmillTraderTradingAllowed: Boolean(tmtInfo?.tradingAllowed),
      }
    },
    []
  )

  const { data: accountInfo = [], isLoading } = useQuery({
    queryKey: queryKeys.accountInfo.limits(),
    queryFn: () => apiClient.getTradingAccountsAccountInfo(),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchInterval: 10 * 60_000,
    enabled: !!auth?.id,
  })

  const accountInfoLimits = useMemo(
    () =>
      accountInfo.length
        ? processAccountInfo(accountInfo, product)
        : initialAccountInfoLimitsState.accountInfoLimits,
    [accountInfo, processAccountInfo, product]
  )

  const data = useMemo<AccountInfoLimitsState>(
    () => ({
      accountInfoLimits,
      accountInfo,
      isLoading,
      lastUpdate: new Date(),
    }),
    [accountInfoLimits, accountInfo, isLoading]
  )

  const refreshLimits = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.accountInfo.limits() })
  }, [queryClient])

  return (
    <AccountInfoLimitsContext.Provider value={{ data, refreshLimits }}>
      {children}
    </AccountInfoLimitsContext.Provider>
  )
}

export const useAccountInfo = (): AccountInfoContextHook => {
  const context = useContext(AccountInfoLimitsContext)
  if (context === undefined) {
    throw new Error('useAccountInfo must be used within a AccountInfoLimitsContextProvider')
  }

  return {
    accountInfoLimits: context.data.accountInfoLimits,
    accountInfo: context.data.accountInfo,
    refreshLimits: context.refreshLimits,
    isLoading: context.data.isLoading,
  }
}
